export type PrivilegeLevel = "none" | "read" | "update" | "manage";

export interface EntityPrivilegeState {
  entity: string;
  level: PrivilegeLevel;
}

export interface ParsedPrivilege {
  action: string;
  entity: string;
  original: string;
}

const ACTION_ENTITY_REGEX = /^(READ|CREATE|UPDATE|DELETE)_(.+)$/;

const SPECIAL_PRIVILEGES = new Set(["SUPER_ADMIN", "ADMIN_ACCESS"]);

export function isSpecialPrivilege(privilegeName: string): boolean {
  return SPECIAL_PRIVILEGES.has(privilegeName);
}

export function parsePrivilegeName(privilegeName: string): ParsedPrivilege | null {
  const match = privilegeName.match(ACTION_ENTITY_REGEX);
  if (!match) return null;
  return {
    action: match[1],
    entity: match[2],
    original: privilegeName,
  };
}

export function extractEntities(privilegeNames: string[]): string[] {
  const entities = new Set<string>();
  for (const name of privilegeNames) {
    const parsed = parsePrivilegeName(name);
    if (parsed) {
      entities.add(parsed.entity);
    }
  }
  return Array.from(entities).sort();
}

export function getActionsForEntity(privilegeNames: string[], entity: string): string[] {
  return privilegeNames
    .filter((name) => {
      const parsed = parsePrivilegeName(name);
      return parsed && parsed.entity === entity;
    })
    .map((name) => parsePrivilegeName(name)!.action);
}

export function determineLevel(actions: string[]): PrivilegeLevel {
  if (actions.length === 0) return "none";

  const actionSet = new Set(actions);

  if (
    actionSet.has("READ") &&
    actionSet.has("CREATE") &&
    actionSet.has("UPDATE") &&
    actionSet.has("DELETE")
  ) {
    return "manage";
  }

  if (actionSet.has("READ") && actionSet.has("UPDATE")) {
    return "update";
  }

  if (actionSet.has("READ")) {
    return "read";
  }

  return "none";
}

export function privilegesForLevel(entity: string, level: PrivilegeLevel): string[] {
  switch (level) {
    case "none":
      return [];
    case "read":
      return [`READ_${entity}`];
    case "update":
      return [`READ_${entity}`, `UPDATE_${entity}`];
    case "manage":
      return [`READ_${entity}`, `CREATE_${entity}`, `UPDATE_${entity}`, `DELETE_${entity}`];
  }
}

export function computePrivilegeState(allPrivilegeNames: string[]): EntityPrivilegeState[] {
  const entities = extractEntities(allPrivilegeNames);
  return entities.map((entity) => ({
    entity,
    level: "none" as PrivilegeLevel,
  }));
}

export function getInitialState(allPrivilegeNames: string[]): Map<string, PrivilegeLevel> {
  const entities = extractEntities(allPrivilegeNames);
  const state = new Map<string, PrivilegeLevel>();
  for (const entity of entities) {
    state.set(entity, "none");
  }
  return state;
}

export function getStateFromExistingPrivileges(
  allPrivilegeNames: string[],
  existingPrivilegeNames: string[]
): Map<string, PrivilegeLevel> {
  const state = getInitialState(allPrivilegeNames);
  const existingParsed = existingPrivilegeNames
    .map((name) => parsePrivilegeName(name))
    .filter((p): p is ParsedPrivilege => p !== null);

  const entityActions = new Map<string, Set<string>>();
  for (const parsed of existingParsed) {
    if (!entityActions.has(parsed.entity)) {
      entityActions.set(parsed.entity, new Set());
    }
    entityActions.get(parsed.entity)!.add(parsed.action);
  }

  for (const [entity, actions] of entityActions) {
    if (state.has(entity)) {
      state.set(entity, determineLevel(Array.from(actions)));
    }
  }

  return state;
}

export function aggregatePrivileges(
  entityStates: Map<string, PrivilegeLevel>,
  specialPrivileges: Set<string>
): string[] {
  const privileges: string[] = [];

  for (const [entity, level] of entityStates) {
    privileges.push(...privilegesForLevel(entity, level));
  }

  for (const special of specialPrivileges) {
    privileges.push(special);
  }

  return privileges;
}

export function getSpecialPrivilegesFromExisting(existingPrivilegeNames: string[]): Set<string> {
  return new Set(existingPrivilegeNames.filter(isSpecialPrivilege));
}

const ENTITY_GROUPS: Array<{ entities: string[]; label: string }> = [
  { entities: ["USER", "USERS_PHONE", "USER_ROLE"], label: "Gestión de Usuarios" },
  {
    entities: ["ORGANIZATION", "ORGANIZATIONS_USER", "GROUPS_ORGANIZATION", "ORGANIZATION_DYNAMIC_PROPERTY"],
    label: "Gestión de Organizaciones",
  },
  { entities: ["ROLE", "ROLES_PRIVILEGE", "PRIVILEGE"], label: "Roles y Privilegios" },
  {
    entities: [
      "PROGRAM",
      "PROGRAM_LEVEL",
      "PROGRAMS_TYPE",
      "PROGRAMS_VERSION",
      "PROGRAM_VERSION_ITEM",
    ],
    label: "Gestión de Programas",
  },
  {
    entities: [
      "INSPECTION",
      "INSPECTIONS_PROGRAM",
      "INSPECTIONS_INSPECTOR",
      "INSPECTIONS_PROGRAMS_ITEM",
      "INSPECTIONS_PROGRAMS_ITEMS_FILE",
    ],
    label: "Gestión de Inspecciones",
  },
  {
    entities: ["INSPECTIONS_FINDING", "INSPECTIONS_FINDINGS_ITEM", "INSPECTIONS_FINDINGS_FILE"],
    label: "Gestión de Hallazgos",
  },
  {
    entities: [
      "TRAINING",
      "TRAINING_MODULE",
      "TRAINING_MODULE_TOPIC",
      "TRAINING_MODULE_TOPIC_CONTENT",
      "TRAINING_ENROLLMENT",
      "TRAINING_CONTENT_PROGRESS",
    ],
    label: "Gestión de Capacitaciones",
  },
  { entities: ["FORM"], label: "Formularios" },
  { entities: ["GOAL", "GOALS_PROGRAM", "GROUP", "GROUPS_GOAL"], label: "Objetivos y Grupos" },
  { entities: ["REPORT_TEMPLATE", "REPORT"], label: "Reportes" },
  { entities: ["ANALYTICS"], label: "Analíticas" },
  { entities: ["SYNC_CONFIG"], label: "Sincronización" },
  { entities: ["ACTIVITY_FEED"], label: "Registro de Actividad" },
  { entities: ["FILE_UPLOAD"], label: "Archivos" },
  { entities: ["FARM_LOCATION", "FARM_SOCIOECONOMIC"], label: "Ubicación de Granjas" },
  { entities: ["CITY", "CORREGIMIENTO", "VEREDA", "REGION", "COUNTRY"], label: "Geografía" },
  { entities: ["ID_TYPE", "MEASUREMENT_TYPE", "DATA_TYPE", "DYNAMIC_PROPERTY_TYPE"], label: "Configuración" },
];

const ENTITY_TO_CATEGORY = new Map<string, string>();
for (const group of ENTITY_GROUPS) {
  for (const entity of group.entities) {
    ENTITY_TO_CATEGORY.set(entity, group.label);
  }
}

export function groupEntitiesByCategory(entities: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();

  const categoryOrder = ENTITY_GROUPS.map((g) => g.label);

  for (const entity of entities) {
    const category = ENTITY_TO_CATEGORY.get(entity) || "Otros";
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category)!.push(entity);
  }

  const ordered = new Map<string, string[]>();
  for (const cat of categoryOrder) {
    if (groups.has(cat)) {
      ordered.set(cat, groups.get(cat)!);
    }
  }
  if (groups.has("Otros")) {
    ordered.set("Otros", groups.get("Otros")!);
  }

  return ordered;
}

export function filterEntities(entities: string[], searchTerm: string): string[] {
  if (!searchTerm.trim()) return entities;
  const lower = searchTerm.toLowerCase();
  return entities.filter((entity) => entity.toLowerCase().includes(lower));
}
